/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type ReactNode } from 'react'
import type { FormInstance } from 'antd'
import type { PlatformType } from '@/types/authorization'
import type { AdvicePrepAppliedCoupon } from '@/features/adviceprep/pricing'
import type { SignupCustomerForm, SignupPricingForm, SignupProductSelection } from '../types'

export type FlowStep = 'product' | 'info' | 'pricing' | 'payment' | 'login' | 'importData' | 'success'

export type SignupAuthenticationOptions = {
  hydrateSession?: boolean
  navigateOnSuccess?: boolean
}

export type SignupAuthenticationTokens = {
  idToken: string
  refreshToken: string
  expiry: number
}

export type SignupContextValue = {
  step: FlowStep
  setStep: (s: FlowStep) => void
  visibleSteps: FlowStep[]
  loading: boolean
  setLoading: (v: boolean) => void
  error: string | null
  setError: (v: string | null) => void
  info: string | null
  setInfo: (v: string | null) => void
  isBasSignup: boolean
  isAdvicePrepSignup: boolean
  advicePrepConnections: number
  setAdvicePrepConnections: (v: number) => void
  showAdvicePrepConfirmModal: boolean
  setShowAdvicePrepConfirmModal: (v: boolean) => void
  advicePrepPendingForm: SignupCustomerForm | null
  advicePrepDuplicate: boolean
  setAdvicePrepDuplicate: (v: boolean) => void
  advicePrepStripeCustomerId: string
  openAdvicePrepConfirm: (values: SignupCustomerForm) => void
  confirmAdvicePrepAccount: () => Promise<void>
  hideBackButton: boolean
  nocc: boolean
  isChildClient: boolean
  setIsChildClient: (v: boolean) => void
  clientSignupCompleted: boolean
  setClientSignupCompleted: (v: boolean) => void
  productSelection: SignupProductSelection | null
  setProductSelection: (v: SignupProductSelection | null) => void
  customerForm: SignupCustomerForm | null
  setCustomerForm: (v: SignupCustomerForm | null) => void
  pricingForm: SignupPricingForm
  setPricingForm: (v: SignupPricingForm) => void
  companyExists: boolean
  setCompanyExists: (v: boolean) => void
  emailExists: boolean
  setEmailExists: (v: boolean) => void
  existingCompanyUser: string
  setExistingCompanyUser: (v: string) => void
  showExistingUserModal: boolean
  setShowExistingUserModal: (v: boolean) => void
  showBasExistsModal: boolean
  setShowBasExistsModal: (v: boolean) => void
  skipPayment: boolean
  setSkipPayment: (v: boolean) => void
  activeUserOptions: { value: string; label: string; price: string }[]
  setActiveUserOptions: (v: { value: string; label: string; price: string }[]) => void
  couponValid: boolean
  setCouponValid: (v: boolean) => void
  trialDuration: number | null
  setTrialDuration: (v: number | null) => void
  advicePrepCoupon: AdvicePrepAppliedCoupon | null
  setAdvicePrepCoupon: (v: AdvicePrepAppliedCoupon | null) => void
  platformId: string
  setPlatformId: (v: string) => void
  isConnectingData: boolean
  setIsConnectingData: (v: boolean) => void
  isCheckingConflicts: boolean
  setIsCheckingConflicts: (v: boolean) => void
  productForm: FormInstance<{ product: PlatformType }>
  customerInfoForm: FormInstance<SignupCustomerForm>
  pricingInfoForm: FormInstance<SignupPricingForm>
  loginForm: FormInstance<{ email: string; password: string }>
  runAction: (task: () => Promise<void>, clearInfo?: boolean) => Promise<void>
  establishSignupSession: (
    email: string,
    tokens: SignupAuthenticationTokens,
    options?: SignupAuthenticationOptions
  ) => Promise<void>
  createAccountWithoutPayment: (payload: SignupCustomerForm, child: boolean) => Promise<void>
  ensureActiveAndAuthenticated: (email: string, password: string) => Promise<void>
  saveTokens: (email: string, idToken: string, refreshToken: string, expiry: number) => Promise<void>
  hydrateSessionAndNavigate: (email: string) => Promise<void>
  finalizeSignup: () => Promise<void>
  popupRef: React.MutableRefObject<Window | null>
  navigate: (to: string, options?: { replace?: boolean }) => void
}

const SignupContext = createContext<SignupContextValue | null>(null)

export function useSignup() {
  const ctx = useContext(SignupContext)
  if (!ctx) throw new Error('useSignup must be used within SignupProvider')
  return ctx
}

export function SignupProvider({ value, children }: { value: SignupContextValue; children: ReactNode }) {
  return <SignupContext.Provider value={value}>{children}</SignupContext.Provider>
}
